import { LazyLoader, SliderModal, Table, TableColumns } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { useImportableLessonsForTimetableVersionQuery } from '../../../../types/planung-graphql-client-defs';
import { selectedToData } from '../../../../utils/selectedToData';
import { useLessonStore } from '../../TimetableVersion/TimetableVersionLessonProvider';
import { type ImportMode, ImportModeChooser } from './ImportModeChooser';

export type LessonsImportProps = {
  versionUuid?: string;
  onClose: () => void;
  onSave: (response: { error?: unknown; success?: boolean; data: string[] }) => void;
  isOpen: boolean;
  subjectContainerLessons: boolean;
};

export type ImportLessonsType = {
  uuid: string;
  subject: string;
  classes: string;
  teachers: string;
  elective: boolean;
  teachingLoadEnabled: boolean;
  timetableEnabled: boolean;
};

export const ImportLessonsToTimetableVersionModal = observer(
  ({ versionUuid, onClose, isOpen, onSave, subjectContainerLessons }: LessonsImportProps) => {
    const { pimAuthClaims } = useAuthClaims();

    const lessonStore = useLessonStore();
    const _lessons = lessonStore.lessons;
    const currentLessonsArray = Array.from(_lessons.values());

    const schoolYear = useSelectedSchoolYear();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);

    // Slide 0 = Auswahl Importieren/Verknüpfen, Slide 1 = Liste der Zuordnungen.
    const [slideIndex, setSlideIndex] = useState(0);
    const [mode, setMode] = useState<ImportMode | null>(null);

    const [selected, setSelected] = useState({});
    const [sorting, onSorting] = useState([
      { id: 'classes', desc: false },
      { id: 'subject', desc: false },
    ]);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

    const lessonContext = useMemorizedCacheTag('LESSONS');

    const [{ data: lessonsData }] = useImportableLessonsForTimetableVersionQuery({
      variables: { versionUuid: versionUuid ?? '', schoolYearUuid: schoolYear?.uuid ?? '' },
      context: lessonContext,
      pause: !versionUuid || !schoolYear?.uuid,
    });

    // Schließt das Modal und setzt den lokalen State zurück (Auswahl-Slide, Modus, Selektion).
    // Alle Schließ-Pfade (Abbrechen, X, erfolgreicher Import) laufen hierüber, damit das nächste
    // Öffnen wieder bei der Auswahl startet.
    const handleClose = () => {
      setSlideIndex(0);
      setMode(null);
      setSelected({});
      onClose();
    };

    const handleChoose = (chosen: ImportMode) => {
      setMode(chosen);
      setSlideIndex(1);
    };

    const handleSubmit = async () => {
      if (!mode) return;
      setLoading(true);
      const selectedLessons = selectedToData<ImportLessonsType>(selected, memorizedLessonsData);
      const uuids = selectedLessons.map((r) => r.uuid);

      try {
        if (!versionUuid) {
          throw new Error('version not found');
        }
        const organizationUuid = pimAuthClaims.getOrganizationUuid();
        const nodeUuids =
          mode === 'import'
            ? await lessonStore.importLessonsToVersion(uuids, versionUuid, organizationUuid)
            : await lessonStore.addLessonsToVersion(uuids, versionUuid, organizationUuid);
        onSave({ success: true, data: nodeUuids });
        handleClose();
      } catch (error) {
        onSave({ error, success: false, data: [] });
      } finally {
        setLoading(false);
      }
    };

    const memorizedLessonsData: ImportLessonsType[] = useMemo(() => {
      return (
        lessonsData?.importableLessonsForTimetableVersion
          .filter((lessonDataToFilter) => {
            if (subjectContainerLessons) {
              /**
               * Es können nur Schienen ausgewählt werden, für die es noch mindestens eine freie Kombination gibt aus:
               * - Selbe Schiene
               * - Selbe Jahrgangsstufen
               * - Selber Zug
               */
              const matchingLesson = currentLessonsArray.find((currentLesson) => {
                return currentLesson.classes.some((currentLessonClassItem) => {
                  return lessonDataToFilter.lessonClasses.some((dataToFilterClass) => {
                    return (
                      dataToFilterClass.class.uuid === currentLessonClassItem.uuid &&
                      currentLesson.subjectUuid === lessonDataToFilter.subject.uuid &&
                      dataToFilterClass.class.gradeGroup?.uuid === currentLessonClassItem.gradeGroup?.uuid
                    );
                  });
                });
              });
              if (matchingLesson) {
                return false;
              }
              return lessonDataToFilter.subject.isSubjectContainer;
            }
            return !lessonDataToFilter.subject.isSubjectContainer;
          })
          .map((lesson): ImportLessonsType => {
            return {
              subject: lesson.subject.name,
              uuid: lesson.uuid,
              classes: lesson.lessonClasses
                .map((lessonClass) => {
                  return lessonClass.class.shortName;
                })
                .join(', '),
              teachers: lesson.teachers
                .map((teacher) => {
                  return teacher.displayNameShort || teacher.shortName;
                })
                .join(', '),
              elective: lesson.elective ?? false,
              teachingLoadEnabled: lesson.teachingLoadEnabled ?? false,
              timetableEnabled: lesson.timetableEnabled ?? false,
            };
          }) ?? []
      );
    }, [currentLessonsArray, lessonsData?.importableLessonsForTimetableVersion, subjectContainerLessons]);

    const tableColumns = useMemo((): TableColumns<ImportLessonsType>[] => {
      return [
        {
          header: t('lesson.table.subject'),
          id: 'subject',
          accessorKey: 'subject',
          meta: {
            filterName: t('lesson.table.subject'),
          },
          size: 200,
          canExpand: true,
        },
        {
          header: t('lesson.table.classes'),
          id: 'classes',
          accessorKey: 'classes',
          size: 50,
          canExpand: true,
        },
        {
          header: t('lesson.table.teacher'),
          id: 'teachers',
          accessorKey: 'teachers',
          size: 175,
          canExpand: true,
        },
        {
          header: t('lesson.basics.isElective'),
          id: 'elective',
          accessorKey: 'elective',
          type: 'boolean',
          size: 60,
        },
        {
          header: t('lesson.basics.isTeachingLoadEnabled.short'),
          meta: {
            filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
            tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
          },
          id: 'teachingLoadEnabled',
          accessorKey: 'teachingLoadEnabled',
          type: 'boolean',
          size: 60,
        },
        {
          header: t('lesson.basics.isTimetableEnabled.short'),
          meta: {
            filterName: t('lesson.basics.isTimetableEnabled.full'),
            tooltip: t('lesson.basics.isTimetableEnabled.full'),
          },
          id: 'timetableEnabled',
          accessorKey: 'timetableEnabled',
          type: 'boolean',
          size: 60,
        },
      ];
    }, [t]);

    const chooserSlide = <ImportModeChooser onChoose={handleChoose} />;

    const listSlide = (
      <div>
        <Suspense fallback={<LazyLoader embedded forceHeight='30vh' />}>
          {isOpen && (
            <Table<ImportLessonsType>
              canScroll={false}
              maxHeight={maxHeight}
              isOnWhite={true}
              rowSelection={selected}
              onRowSelectionChange={setSelected}
              showActionBar
              actionBarSettings={{
                showExpertFilter: true,
                showAddButton: false,
                showPrintButton: false,
                showBulkEdit: false,
                showSelectedRowsCount: true,
              }}
              sorting={sorting}
              onSortingChange={onSorting}
              columns={tableColumns}
              data={memorizedLessonsData ?? []}
              showSelect
            />
          )}
        </Suspense>
      </div>
    );

    const submitText =
      mode === 'import'
        ? t('lesson.importModal.options.import.button')
        : t('lesson.importModal.options.connect.button');

    return (
      <SliderModal
        isOpen={isOpen}
        title={t('lesson.importModal.title')}
        slides={[chooserSlide, listSlide]}
        defaultIndex={slideIndex}
        // Auswahl-Slide hat eigene Buttons – Footer-Steuerung nur auf der Listen-Slide zeigen.
        hideControls={slideIndex === 0}
        submitText={submitText}
        submitDisabled={Object.keys(selected).length === 0 || loading}
        loading={loading}
        onChange={(index) => setSlideIndex(index)}
        onPrevious={(index) => setSlideIndex(index)}
        onClose={() => handleClose()}
        onSubmit={() => void handleSubmit()}
        modalSettings={{
          height: 'l',
          shouldCloseOnEsc: false,
          shouldCloseOnOverlayClick: false,
          onAfterOpen: (options) => {
            if (options?.contentEl.clientHeight) {
              setMaxHeight(options?.contentEl.clientHeight);
            }
          },
        }}
      />
    );
  },
);
